import { useState } from 'react';
import { X, ZoomIn } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const portfolioImages = [
  {
    url: "https://customer-assets.emergentagent.com/job_werbetechnik-landing/artifacts/e46cdzga_IMG_2838.jpeg",
    alt: "Fahrzeugdesign zur Selbstmontage - MS-Galabau",
    category: "Fahrzeugdesign"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_werbetechnik-landing/artifacts/n8atgxg4_IMG_2839.jpeg",
    alt: "Transporterbeschriftung MS-Galabau",
    category: "Flottenbeschriftung"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/t5j1gj3o_b585fa77-fc41-46ac-b7a6-7f36b4842aae.jpeg",
    alt: "Ford Mustang Racing Stripes",
    category: "Fahrzeugdesign"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/civww0ec_c2de06ca-2f26-4c7f-a997-2f07da68243b.jpeg",
    alt: "MS-Galabau Transporter Beschriftung",
    category: "Firmenbeschriftung"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/3ag4pchx_20434063-2cbe-4712-945e-dc91e5e58535.jpeg",
    alt: "Autozentrum B&A - Außenwerbung",
    category: "Werbebeschriftung"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/wubathrs_503cbb1e-3a8f-48fe-ac8d-4f93d90ad452.jpeg",
    alt: "Ford Mustang GT - Rennstreifen Design",
    category: "Fahrzeugdesign"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/gdfa6jdo_b5a11187-78e2-4c3d-a5c4-f4c43a965415.jpeg",
    alt: "Design Mockup Service",
    category: "Design Service"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/tdl945qz_e2640680-cab8-4af5-aa77-b639957314ec.jpeg",
    alt: "Schaufensterbeschriftung ANKAUF",
    category: "Schaufenster"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_67b9bb81-7260-44ec-aaa3-ea9719c1ace2/artifacts/qk9p2bzd_6c2c4441-17e3-424f-961f-de25596bfaab.jpeg",
    alt: "Werbebeschriftung - Professionelle Außenwerbung",
    category: "Werbebeschriftung"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_edc9e93e-c807-4533-99b3-f9df4cff492a/artifacts/6qg9zhs4_image0%20%286%29.jpeg",
    alt: "BMW M2 - Folierung in der Werkstatt",
    category: "Fahrzeugfolierung"
  }
];

export const Portfolio = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section
      id="referenzen"
      className="py-24 lg:py-32 bg-slate-50 noise-bg"
      data-testid="portfolio-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-700 text-sm font-semibold rounded-full mb-4">
            REFERENZEN
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Unsere Arbeit
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ein paar Beispiele aus unseren Projekten.
          </p>
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioImages.map((image, index) => (
            <div
              key={index}
              className="group relative aspect-[4/3] overflow-hidden rounded-2xl cursor-pointer card-hover"
              onClick={() => setSelectedImage(image)}
              data-testid={`portfolio-item-${index}`}
            >
              <img
                src={image.url}
                alt={image.alt}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-3 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full mb-2">
                    {image.category}
                  </span>
                  <p className="text-white font-medium">{image.alt}</p>
                </div>
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <ZoomIn className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl p-0 bg-transparent border-none shadow-none [&>button]:hidden">
          <VisuallyHidden>
            <DialogTitle>{selectedImage?.alt || 'Bildansicht'}</DialogTitle>
            <DialogDescription>Großansicht des Portfoliobildes</DialogDescription>
          </VisuallyHidden>
          <div className="relative">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:text-teal-400 transition-colors"
              data-testid="lightbox-close"
              aria-label="Schließen"
            >
              <X className="w-8 h-8" />
            </button>
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.alt}
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
